import type {Options, SeriesOptionsType} from 'highcharts';
import CubeIndex, {AxisItem} from '../cube/CubeIndex';
import {axisLabelFormat, categoryLabel, escapeHtml, formatValue, tooltipHtml} from '../render/format';
import {isLowBase, lowBaseAttributes} from '../render/lowBase';
import {MarkRenderer, MEASURES_CHANNEL} from '../types';

const LIGHT_END = '#F2F2F2';

const renderHeatmap: MarkRenderer = (cube, spec, ctx) => {
    const index = new CubeIndex(cube);
    const {x, y, color} = spec.encoding;
    const xChannel = typeof x === 'string' ? x : MEASURES_CHANNEL;
    const yChannel = typeof y === 'string' ? y : MEASURES_CHANNEL;
    const xItems = index.items(xChannel);
    const yItems = index.items(yChannel);
    const colorMeasure = index.measure(typeof color === 'string' ? color : cube.measures[0]?.id);
    const options = spec.options;

    const positions = (xItem: AxisItem, yItem: AxisItem): Record<string, number> => ({
        ...(xChannel !== MEASURES_CHANNEL ? {[xChannel]: xItem.index} : {}),
        ...(yChannel !== MEASURES_CHANNEL ? {[yChannel]: yItem.index} : {}),
    });

    const data: Record<string, any>[] = [];
    yItems.forEach((yItem, yIndex) => xItems.forEach((xItem, xIndex) => {
        // a $measures axis carries the measure itself; otherwise the colour measure fills every cell
        const measure = xItem.measure ?? yItem.measure ?? colorMeasure;
        const cell = index.value(positions(xItem, yItem), measure.id);
        const lowBase = isLowBase(cube, cell);

        data.push({
            x: xIndex,
            y: yIndex,
            value: cell?.value ?? null,
            ...(lowBase ? lowBaseAttributes('bar', ctx.palette[0], ctx) : {}),
            custom: {
                count: cell?.count ?? 0,
                lowBase,
                valueText: formatValue(cell?.value, measure, ctx),
                tooltip: tooltipHtml({heading: [yItem.label, xItem.label], measure, cell, lowBase}, ctx),
            },
        });
    }));

    const colorAxis: Record<string, any> = {
        stops: [[0, ctx.dark ? '#2A2A2A' : LIGHT_END], [1, colorMeasure.color ?? ctx.palette[0]]],
        labels: {format: axisLabelFormat(colorMeasure)},
    };
    // Total row/column would dominate the scale (counts), so the range comes from the inner cells only
    const isTotal = (item: AxisItem) => item.member?.role === 'total';
    const innerValues = data
        .filter(point => !isTotal(xItems[point.x]) && !isTotal(yItems[point.y]))
        .map(point => point.value)
        .filter((value): value is number => typeof value === 'number');
    const hasTotals = xItems.some(isTotal) || yItems.some(isTotal);
    if (typeof options.colorMin === 'number') {
        colorAxis.min = options.colorMin;
    } else if (hasTotals && innerValues.length) {
        colorAxis.min = Math.min(...innerValues);
    }
    if (typeof options.colorMax === 'number') {
        colorAxis.max = options.colorMax;
    } else if (hasTotals && innerValues.length) {
        colorAxis.max = Math.max(...innerValues);
    }

    const series = {
        type: 'heatmap',
        id: colorMeasure.id,
        name: escapeHtml(colorMeasure.label),
        data,
        borderWidth: 1,
        borderColor: ctx.dark ? '#1E1E1E' : '#FFFFFF',
        // heatmap cells are drawn as markers, so the shared circle symbol from baseOptions would turn them into dots
        marker: {symbol: 'rect'},
    } as unknown as SeriesOptionsType;

    return {
        type: 'highcharts',
        options: {
            chart: {type: 'heatmap'},
            title: {text: undefined},
            credits: {enabled: false},
            xAxis: {categories: xItems.map(categoryLabel)},
            // first member on top, like a table
            yAxis: {categories: yItems.map(categoryLabel), title: {text: undefined}, reversed: true},
            colorAxis,
            legend: {enabled: true},
            tooltip: {headerFormat: '', pointFormat: '{point.custom.tooltip}'},
            // on plotOptions.series, not the series, so the shared data-label toggle can override them
            plotOptions: {series: {dataLabels: {enabled: options.dataLabels !== false, format: '{point.custom.valueText}'}}},
            series: [series],
        } as Options,
    };
};

export default renderHeatmap;
